/**
 * Daily dispatch API helpers.
 *
 * Prefer the named `API_PATHS.DISPATCH` wrappers in this file for new code.
 * Functions after the legacy banner are DEPRECATED outdated dispatchv2.js
 * calls (some still in use). Do not use them in new code; add a current
 * wrapper above instead. They will be removed.
 */

import { getAPIData, apiRequest } from "./axios";
import { API_PATHS } from "../../config/apiConfig";
import { getDateString } from "../../utils/datetime";

const DEFAULT_WEATHER_LOCATION = "San Francisco, CA";

/**
 * Return a geocodable location for weather lookup.
 *
 * Path-like values such as `.` / `..` are not valid addresses and used to
 * be interpolated into the weather URL path, which browsers resolve as
 * directory traversal and Django then 404s.
 *
 * @param {string} jobAddress
 * @returns {string}
 */
function resolveWeatherAddress(jobAddress) {
  const address = typeof jobAddress === "string" ? jobAddress.trim() : "";
  if (!address || address === "." || address === "..") {
    return DEFAULT_WEATHER_LOCATION;
  }
  return address;
}

/**
 * Current weather for a job report date and address.
 *
 * @param {string} date YYYY-MM-DD
 * @param {string} jobAddress Job default address; invalid values fall back to SF.
 * @returns {Promise<Object|null>} WeatherAPI current-weather payload, or null on failure.
 */
export async function getWeatherData(date, jobAddress) {
  const url = API_PATHS.DISPATCH.WEATHER_DATA(
    date,
    resolveWeatherAddress(jobAddress)
  );
  try {
    return await getAPIData(url);
  } catch {
    return null;
  }
}

/**
 * Fetches DayDetails for each calendar day from `startIso` through `endIso` (inclusive).
 * Dates must be `YYYY-MM-DD` (local calendar keys).
 *
 * @param {string} startIso
 * @param {string} endIso
 * @returns {Promise<Array>}
 */
export async function getDayDetails(startIso, endIso) {
  const qs = new URLSearchParams({ start: startIso, end: endIso });
  const url = `${API_PATHS.DISPATCH.DAY_DETAILS}?${qs.toString()}`;
  return await getAPIData(url);
}

/**
 * Catalog/reference rows used to populate dispatch pickers (scopes, units, etc.).
 *
 * @returns {Promise<Object>}
 */
export async function getModelReferenceData() {
  const url = API_PATHS.DISPATCH.MODEL_REFERENCE;
  return await getAPIData(url);
}

/**
 * Fetch full project concrete-pour entries (filter Type/Range on the client).
 *
 * @param {Object} params
 * @param {number|string} params.job - JobNumber primary key (required).
 * @param {Object} [config] - Optional axios config (e.g. ``signal`` for abort).
 * @returns {Promise<Object>} ``{ job, entries }``.
 */
export async function getProjectConcretePours({ job } = {}, config = {}) {
  const params = new URLSearchParams();
  if (job != null) {
    params.set("job", String(job));
  }
  const query = params.toString();
  const url = `${API_PATHS.DISPATCH.PROJECT_CONCRETE_POURS}${query ? `?${query}` : ""}`;
  return await apiRequest(url, null, { method: "GET", ...config });
}

/**
 * Create a WorkScopeAspects row for the dispatch day.
 *
 * @param {Object} scope Aspect payload accepted by the add-scope endpoint.
 * @returns {Promise<Object>}
 */
export async function addWorkScopeAspect(scope) {
  const url = API_PATHS.DISPATCH.ADD_WORK_SCOPE;
  const body = { scope };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Copy scopes from an origin job/day onto target jobs and date.
 *
 * @param {string} type Duplicate mode accepted by the backend.
 * @param {*} origin Source job/scope identifier.
 * @param {Array<number|string>} targetJobIds Destination job PKs.
 * @param {string} targetDate YYYY-MM-DD destination date.
 * @returns {Promise<Object>}
 */
export async function duplicateScopes(type, origin, targetJobIds, targetDate) {
  const url = API_PATHS.DISPATCH.DUPLICATE_SCOPES;
  const body = { type, origin, targetJobIds, targetDate };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Patch DayDetails worker list for one calendar date.
 *
 * @param {string} date Date key used by PATCH_DAY_DETAILS.
 * @param {number[]} workerIds Assigned worker PKs.
 * @returns {Promise<Object>}
 */
export async function patchDayDetails(date, workerIds) {
  const url = API_PATHS.DISPATCH.PATCH_DAY_DETAILS(date);
  const body = { workerIds };
  return await apiRequest(url, body, { method: "PATCH" });
}

/**
 * Update scope detail fields (notes, extras) for one aspect.
 *
 * @param {number|string} scopeId WorkScopeAspects PK.
 * @param {Object} payload Partial detail fields.
 * @returns {Promise<Object>}
 */
export async function updateScopeDetails(scopeId, payload) {
  const url = API_PATHS.DISPATCH.UPDATE_SCOPE_DETAILS;
  const body = { scopeId, payload };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Update a WorkScopeAspects row from the dispatch UI (dispatch-module-only).
 *
 * Cross-module worker/aspect patches belong in `services/api/planning.js`.
 *
 * @param {number|string} scopeId WorkScopeAspects PK.
 * @param {Object} payload Partial aspect fields.
 * @returns {Promise<Object>}
 */
export async function updateWorkScopeAspect(scopeId, payload) {
  const url = API_PATHS.DISPATCH.UPDATE_WORK_SCOPE;
  const body = { scopeId, payload };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Record a dispatch stage change (Draft / Dispatched / Reviewed / Modified / Broadcast).
 *
 * @param {string} stage Stage label.
 * @param {string} date Calendar date for the DayDetails row.
 * @param {Object|null} [checks=null] Optional review-checklist payload.
 * @param {Object} [additionalInfo={}] JSON-serializable object forwarded as `additional_info`.
 *   Backend stores it as-is. Frontend interprets keys from `stage`.
 *   Broadcast: `{ message: string, userIds: number[] }`. Omit or `{}` when unused.
 * @returns {Promise<Object>}
 */
export async function updateDispatchStage(stage, date, checks = null, additionalInfo = {}) {
  const url = API_PATHS.DISPATCH.UPDATE_DISPATCH_STAGE;
  const body = { stage, checks, date, additional_info: additionalInfo };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Clear one assignment set (workers, equipment, etc.) on a scope.
 *
 * @param {number|string} scopeId WorkScopeAspects PK.
 * @param {string} setType Assignment collection name.
 * @returns {Promise<Object>}
 */
export async function clearAssignmentSet(scopeId, setType) {
  const url = API_PATHS.DISPATCH.CLEAR_ASSIGNMENT_SET;
  const body = { scopeId, setType };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Clear day-level worker assignments for a dispatch date.
 *
 * @param {string} date Calendar date key.
 * @returns {Promise<Object>}
 */
export async function clearWorkers(date) {
  const url = API_PATHS.DISPATCH.CLEAR_WORKERS;
  const body = { date };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Delete one or more WorkScopeAspects rows from dispatch.
 *
 * @param {Array<number|string>} scopeIds Aspect PKs to delete.
 * @returns {Promise<Object>}
 */
export async function deleteWorkScopeAspects(scopeIds) {
  const url = API_PATHS.DISPATCH.DELETE_WORK_SCOPE_ASPECTS;
  const body = { scopeIds };
  return await apiRequest(url, body, { method: "POST" });
}

// Legacy functions from dispatchv2.js - These are outdated API calls that were made by Ben. 
// Some of them are still in use. These should not be used in new code. 
// Use the new API calls above instead. If something doesn't exist there, build it there. 
// These will be removed in the future.

/**
 * DEPRECATED. Outdated dispatchv2.js call. Do not use in new code; use
 * current dispatch/planning job helpers instead. Still used by some forms.
 *
 * @param {Date|string} weekStart Week start used to build YYYY-MM-DD.
 * @returns {Promise<Array|Object>}
 */
export async function getJobsByWeek(weekStart) {
  const weekStartStr = getDateString(weekStart);
  const url = `/dispatch/getjobs/${weekStartStr}`;
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * DEPRECATED. Outdated dispatchv2.js call. Do not use in new code.
 *
 * @param {Date|string} weekStart Week start used to build YYYY-MM-DD.
 * @returns {Promise<Array|Object>}
 */
export async function getScopesByWeek(weekStart) {
  const weekStartStr = getDateString(weekStart);
  const url = `/dispatch/getscopes/${weekStartStr}`;
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * DEPRECATED. Outdated dispatchv2.js DELETE. Prefer `deleteWorkScopeAspects`.
 *
 * @param {number|string} scopeId WorkScopeAspects PK.
 * @returns {Promise<Object>}
 */
export async function deleteScope(scopeId) {
  const url = `/dispatch/workscopeaspects/${scopeId}/`;
  return await apiRequest(url, null, { method: "DELETE" });
}


/**
 * DEPRECATED. Outdated dispatchv2.js GET for one aspect. Do not use in new code.
 *
 * @param {number|string} scopeId WorkScopeAspects PK.
 * @returns {Promise<Object>}
 */
export async function getScope(scopeId) {
  const url = `/dispatch/workscopeaspects/${scopeId}/`;
  return await apiRequest(url, null, { method: "GET" });
}


// Partial aspect updates live in services/api/planning.js (promoted cross-module API).

// Some requests used to fill in forms

/**
 * DEPRECATED. Outdated form-fill GET for mix designs. Do not use in new code.
 *
 * @returns {Promise<Array>}
 */
export async function getMixDesigns() {
  return await apiRequest("/dispatch/mixdesigns/", null, { method: "GET" });
}

/**
 * DEPRECATED. Outdated form-fill GET for dumping suppliers. Do not use in new code.
 *
 * @returns {Promise<Array>}
 */
export async function getDumpingSuppliers() {
  return await apiRequest("/dispatch/dumpingsuppliers/", null, {
    method: "GET",
  });
}

/**
 * DEPRECATED. Outdated form-fill GET for assets. Do not use in new code.
 *
 * @returns {Promise<Array>}
 */
export async function getAssets() {
  return await apiRequest("/dispatch/assets/", null, { method: "GET" });
}

/**
 * DEPRECATED. Outdated form-fill GET for subcontractors. Do not use in new code.
 *
 * @returns {Promise<Array>}
 */
export async function getsubcontractor() {
  return await apiRequest("/dispatch/subcontractor/", null, { method: "GET" });
}

/**
 * DEPRECATED. Outdated form-fill GET for work-scope catalog. Do not use in new code.
 *
 * @returns {Promise<Array>}
 */
export async function getWorkScopes() {
  return await apiRequest("/dispatch/workscopes/", null, { method: "GET" });
}

/**
 * DEPRECATED. Outdated form-fill GET for addresses. Prefer `getAddresses` in core.js.
 *
 * @returns {Promise<Array>}
 */
export async function getAddresses() {
  return await apiRequest("/dispatch/addresses/", null, { method: "GET" });
}

